import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageAutoGather({ navigate }) {
  return (
    <div>
      <p className="text-sm text-brand-400 font-medium mb-2">Farm</p>
      <h1 className="font-display text-3xl font-bold text-white tracking-tight mb-3">Auto Gather</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Auto Gather harvests plants and ores, opens chests and uses interactive objects (eggs,
        flower piles and the like) as you pass them. Like Auto Loot, it does not walk to anything
        &mdash; it acts on what comes within reach. Everything is on the{' '}
        <strong className="text-white">FARM &rsaquo; Gather</strong> tab.
      </p>

      <Callout type="warning">
        <strong>Gathering uses up tools and keys.</strong> Plants and ores need{' '}
        <strong>Multitools</strong>; many chests need <strong>lockpicks</strong> or event keys. When
        you run out of Multitools, a notification says{' '}
        &ldquo;Out of Multitools - plants and ores are skipped.&rdquo; A chest you cannot afford
        shows up as <strong>Skipped a chest</strong> on the tab.
      </Callout>

      <DocHeading>Turning It On</DocHeading>
      <Step n={1} title="Switch on AUTO GATHER">
        The switch sits on the right of the <strong>AUTO GATHER</strong> card&rsquo;s title row
        &mdash; click anywhere on that row. A <strong>Toggle key</strong> can be bound below it; it
        works even while the feature is off, and only while the game window is in front.
      </Step>
      <Step n={2} title="Set the range">
        <strong>Gather within</strong> in the <strong>RANGE</strong> card: 1 to 6 metres, default
        4 m.
      </Step>
      <Step n={3} title="Choose what to gather">
        In <strong>WHAT TO GATHER</strong>: the <strong>Plants</strong> and <strong>Ores</strong>{' '}
        switches, and two dropdowns where you tick types one by one &mdash;{' '}
        <strong>Chests</strong> and <strong>Interactables</strong>.
      </Step>

      <Shot
        tab="FARM › Gather"
        caption="The Gather tab: status and range on the left, what to gather on the right."
        name="auto-gather-01-gather-tab.png"
        shoot="A FARM › Gather fül bekapcsolt Auto Gather-rel, egy pályán, ahol van a közelben gyűjthető dolog. Balra az AUTO GATHER kártya (zöld kapcsoló a címsorban, Status, Within range „N (of M seen)”, zöld Wanted, Gathered, Nodes on map) és alatta a RANGE kártya (az ADVANCED lehet csukva). Jobbra a WHAT TO GATHER kártya a Plants / Ores kapcsolókkal és a csukott Chests / Interactables legördülőkkel."
      />

      <DocHeading>Which Chests</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Chests are listed one by one because they cost different things to open. Ticked types are
        opened, the rest are walked past. The defaults: every free chest is on, plus the cheap
        5-lockpick chest; anything that costs more is off until you turn it on.
      </p>
      <DocTable
        headers={['Chest type', 'What it costs', 'Default']}
        rows={[
          ['Free chests', 'Nothing', 'On'],
          ['Bloodshed chest', 'Nothing', 'On'],
          ['5 lockpicks', '5 lockpicks', 'On'],
          ['25 lockpicks', '25 lockpicks', 'Off'],
          ['100 lockpicks', '100 lockpicks', 'Off'],
          ['Small amphora (free)', 'Nothing', 'On'],
          ['Amphora (1 key)', '1 key', 'Off'],
          ['Golden amphora (5 keys)', '5 keys', 'Off'],
          ['Parallel world run (free)', 'Nothing', 'On'],
          ['Other (event, quest)', 'Varies — often event keys', 'Off'],
          ['Could not identify', 'Unknown', 'Off'],
        ]}
      />
      <Callout type="info">
        <strong>It checks your keys first.</strong> If a ticked chest needs more lockpicks or keys
        than you carry, it is skipped and the tab shows{' '}
        <strong>Skipped a chest</strong> with what it needed and how many you have &mdash; for about
        ten seconds. If that line keeps coming back, you really are out.
      </Callout>
      <Callout type="info">
        <strong>&ldquo;Could not identify&rdquo; is off on purpose.</strong> If the bot cannot tell
        what a chest is, it cannot tell what opening it costs either &mdash; so it does not open it
        unless you say so.
      </Callout>
      <Callout type="info">
        <strong>Bloodshed chests during a route.</strong> While the Autopilot is running and{' '}
        <button onClick={() => navigate('bloodshed')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Bloodshed</button>{' '}
        is switched on, Auto Gather leaves bloodshed chests to that feature. When you play by hand,
        Auto Gather opens them like any other ticked chest.
      </Callout>

      <DocHeading>Interactables</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Objects that are not plants, ores or chests but that the game lets you click. They do very
        different things, so again each type has its own tick &mdash; and only the free, harmless one
        is on by default.
      </p>
      <DocTable
        headers={['Type', 'What using it does', 'Default']}
        rows={[
          ['Collectibles (eggs, flowers, piles)', 'Collects them — free, often drops loot', 'On'],
          ['Search spots (gold heaps)', 'Searches the spot', 'Off'],
          ['Shrines - buffs, costs an item', 'Gives a buff and uses up an item', 'Off'],
          ['Pinatas - costs a key', 'Opens it and uses up keys', 'Off'],
          ['Summoning objects - spawns monsters', 'Summons monsters', 'Off'],
          ['Portals - moves your character', 'Takes you somewhere else', 'Off'],
          ['Switches and dig sites', 'Activates them', 'Off'],
          ['Other interactables', 'Anything else the game marks as usable', 'Off'],
          ['Quest objects', 'Uses quest objects', 'Off'],
          ['Could not identify', 'Unknown', 'Off'],
        ]}
      />
      <Callout type="warning">
        <strong>Think before ticking Summoning objects or Portals</strong>, especially on an
        Autopilot route: one starts a fight, the other moves your character off the route. For types
        that cost something, the bot checks first that you carry enough.
      </Callout>

      <DocHeading>Gathering on an Autopilot Route</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        On its own, Auto Gather never stops you. When the{' '}
        <button onClick={() => navigate('autopilot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Autopilot</button>{' '}
        is driving, it asks the route to pause briefly while there is something wanted within range,
        so the game has time to respond. The route moves on as soon as nothing is left to do. A
        bloodshed chest gets the full pause, everything else half of it, and a pause never lasts more
        than a few seconds.
      </p>
      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-white">Wait for dropped loot</strong> (under ADVANCED, default on)
        adds one more wait. Some chests and collectibles throw their loot on the ground. Right after
        opening one, the bot watches for a moment; if loot you want lands in reach, the route waits
        until Auto Loot has picked it up &mdash; a few seconds at most (bloodshed chests get the full
        wait, everything else half of it). If nothing lands, it does not slow down at all. This needs{' '}
        <button onClick={() => navigate('auto-loot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Loot</button>{' '}
        switched on &mdash; with Auto Loot off, there is nothing to wait for. Turn it off if you would
        rather keep moving and leave dropped loot behind.
      </p>

      <DocHeading>Range and Timing</DocHeading>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<strong className="text-white">Gather within</strong>, 'How far it may reach: 1 to 6 metres. Default 4 m.'],
          [<strong className="text-white">Delay</strong>, 'Under ADVANCED. The pause between two gather attempts (200–5000 ms). Default 700 ms.'],
          [<strong className="text-white">Wait for dropped loot</strong>, 'Under ADVANCED. See above. Default on.'],
        ]}
      />

      <DocHeading>The AUTO GATHER Card</DocHeading>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<strong className="text-white">Status</strong>, 'What it is doing right now (see below).'],
          [<strong className="text-white">Not gathering</strong>, 'Shown for a moment after a map change, while the game loads the new map and it settles. It clears on its own. If it says it cannot read the map id and the number keeps climbing, report it.'],
          [<strong className="text-white">Within range</strong>, 'Objects inside your range. The number in brackets counts what the bot sees in a wider area around you.'],
          [<strong className="text-white">Wanted</strong>, 'How many of those you actually asked for (and can afford). Orange zero means your ticks exclude everything nearby.'],
          [<strong className="text-white">On cooldown</strong>, 'Shown only when not zero. Objects it has just tried or opened.'],
          [<strong className="text-white">Skipped a chest</strong>, 'A chest or interactable it left because you lack the key: what it needs (by the item’s ID), how many, and how many you have.'],
          [<strong className="text-white">Gathered</strong>, 'Gather commands sent since you started the bot.'],
          [<strong className="text-white">Last send</strong>, 'The game’s answer to the latest attempt, for about ten seconds. “ok” is green; “not openable now” is normal for something already opened or harvested.'],
          [<strong className="text-white">Failed sends</strong>, 'Shown in red only if the bot could not even put a gather command together. This should be rare — if the number keeps climbing, report it.'],
          [<strong className="text-white">Nodes on map</strong>, 'All map objects the bot currently knows about (decoration included), near or far, wanted or not.'],
        ]}
      />

      <DocTable
        headers={['Status', 'What it means']}
        rows={[
          ['Disabled', 'Auto Gather is switched off.'],
          ['Looking for nodes', 'Just switched on; the first scan is running.'],
          ['Nothing within 4.0 m', 'Nothing gatherable inside your range (the number is your range).'],
          ['3 within 4.0 m, none of them wanted', 'There are objects in range, but none match your ticks — or you cannot afford them.'],
          ['2 wanted, all on cooldown', 'Everything wanted has just been tried or opened.'],
          ['Gathering...', 'A command is on its way.'],
          ['Waiting (interval)', 'Waiting for the Delay to pass.'],
          ['Reading the new map', 'You just changed map; the object list is being rebuilt.'],
          ['Not connected', 'You are not connected to a game server.'],
          ['Waiting for game data', 'The bot cannot read the world yet.'],
          ['Disabled: unexpected command layout', 'A game patch changed how gathering works, so the bot stopped itself. Wait for an update.'],
        ]}
      />
      <Callout type="warning">
        <strong>&ldquo;The game refused the gather command: &hellip;&rdquo;</strong> The game turned an
        attempt down, and the notification names the reason. One now and then (for example right
        after a map change) is harmless; the object is tried again. If it repeats every minute on
        the same spot, report it with the reason shown.
      </Callout>

      <DocHeading>Markers in the World</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        While Auto Gather is on, a green circle around your character shows your range, and a
        coloured dot marks the objects whose type you ticked: green for plants, amber for ores,
        blue for chests, violet for interactables. Chests and interactables only get a dot if you
        can also afford them. <strong className="text-white">A chest without a dot is one it will
        skip</strong> &mdash; its type is not ticked, you cannot afford it, or Bloodshed is handling
        it on the route.
      </p>

      <Callout type="info">
        <button onClick={() => navigate('esp')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">ESP</button>{' '}
        can also draw <strong>Ores</strong>, <strong>Plants</strong> and <strong>Chests</strong> from
        further away (its GATHERING card), which is the fastest way to check whether the bot sees
        what you see.
      </Callout>

      <DocHeading>Why Doesn&rsquo;t It Gather That?</DocHeading>
      <DocTable
        headers={['What you see', 'Likely reason']}
        rows={[
          ['Plants and ores are ignored', 'You are out of Multitools (there was a notification), or the Plants / Ores switch is off.'],
          ['A chest is walked past', 'Its type is not ticked in Chests, you lack the key (Skipped a chest), or Bloodshed is handling bloodshed chests on the route.'],
          ['“wanted, all on cooldown” for a while', 'It just tried. Something it opened is left alone for up to a minute (tried again if it is still there a few seconds later); a failed attempt is retried after a short pause.'],
          ['Nothing happens right after a map change', 'It first reads the objects of the new map (Status: Reading the new map). This takes a moment and needs nothing from you.'],
          ['The route does not wait for chest loot', 'Auto Loot is off, Wait for dropped loot is off, or the loot does not match your pickup settings.'],
        ]}
      />
      <Callout type="info">
        Everything on this tab except the on/off switch and the toggle key is saved in a{' '}
        <button onClick={() => navigate('farm-profiles')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Farm Profile</button>,
        so a bloodshed setup and a boss-room setup can each keep their own chest ticks.
      </Callout>
    </div>
  );
}

export default PageAutoGather;
